import React, { useMemo, useState } from 'react'
import { clsx } from 'clsx'
import { SearchableDropdown } from 'design-system/ui/molecules/dropdown-search'
import { ChevronDownIcon } from 'lucide-react'

import type { GetInternalActionExecutionLogByIdResponse } from '~/generated/client'
import { formatTimeInMS } from '~/utils/time'
import {
  blockHeadingStyle,
  blockSubHeadingStyle,
  Dot,
  LineHeading,
} from '../components/common'
import { TabComponent } from '../components/tab'

interface NetworkLog {
  type: string
  request: {
    url: string
    method: string
    headers: Record<string, string>
    params: Record<string, string>
    json: unknown
  }
}

const isNetworkCall = (log: Partial<NetworkLog>): boolean => {
  return !!log?.request?.url
}

export const NetworkLogsDropdown = ({
  selectedCall,
  onSelect,
  logs,
}: {
  selectedCall: number
  onSelect: (index: number) => void
  logs: NetworkLog[]
}) => {
  const items = logs.map((log, i) => ({
    component_type: 'menu_item',
    label: isNetworkCall(log) ? `#${i + 1} Network Call` : `#${i + 1} Log`,
    value: i.toString(),
    rightIcon: null,
  }))

  return (
    <SearchableDropdown
      dataItems={[
        {
          children: items,
          component_type: 'menu_item_group',
        },
      ]}
      onChange={(value: string) => onSelect(parseInt(value))}
      dialogClassName="min-w-[132px]"
      selectedValue={selectedCall.toString()}
    >
      <div className="ds-text-blue-700 flex items-center gap-2 text-[14px] underline">
        <span>
          Showing {selectedCall + 1} of {logs.length} logs
        </span>
        <ChevronDownIcon className="h-4 w-4" />
      </div>
    </SearchableDropdown>
  )
}

export const NetworkNLogsBlockComponent = ({
  data,
}: {
  data: GetInternalActionExecutionLogByIdResponse['steps'][number]
}) => {
  const logs = (data.logs ?? []).filter(
    (log) => log.type === 'network',
  ) as NetworkLog[]
  const [selectedNetworkCallIndex, setSelectedNetworkCallIndex] = useState(0)
  const selectedNetworkCall = logs[selectedNetworkCallIndex]

  const tabPayload = useMemo(() => {
    if (!isNetworkCall(selectedNetworkCall!)) {
      return [
        {
          key: 'request',
          heading: 'Log',
          content: JSON.stringify(selectedNetworkCall, null, 2),
        },
      ]
    }

    return selectedNetworkCall
      ? [
          {
            key: 'request',
            heading: 'REQUEST',
            content: [
              `URL: ${selectedNetworkCall.request.url}`,
              `Method: ${selectedNetworkCall.request.method.toUpperCase()}`,
              `Headers: ${JSON.stringify(selectedNetworkCall.request.headers)}`,
              `Params: ${JSON.stringify(selectedNetworkCall.request.params)}`,
              `Body: ${JSON.stringify(selectedNetworkCall.request.json)}`,
            ].join('\n'),
          },
          {
            key: 'response',
            heading: 'RESPONSE',
            // @ts-expect-error - this is okay for now
            content: JSON.stringify(selectedNetworkCall.response),
          },
        ]
      : []
  }, [selectedNetworkCall])

  if (!logs?.length) {
    return null
  }

  return (
    <div className="ds-flex ds-items-center ds-gap-2 relative w-full">
      <Dot />
      <div className="w-full pl-[8px]">
        <div className="flex w-full flex-col gap-2">
          <div className="flex items-center gap-2">
            <div
              className={clsx(
                blockHeadingStyle,
                'flex w-full items-center justify-between',
              )}
            >
              <LineHeading />
              Executing request on upstream server
              <div className="ds-text-11 ds-font-400 ds-font-mono ds-text-black-900">
                {data.startTime ? formatTimeInMS(new Date(data.startTime)) : ''}
              </div>
            </div>
          </div>
          <div
            className={clsx(blockSubHeadingStyle, 'flex justify-between pr-4')}
          >
            <span>On python server in isolated VM</span>
            <span>Execution time: {data.totalDuration}ms</span>
          </div>

          <div className="ds-mt-12 pr-[12px]">
            <TabComponent tabs={tabPayload} selectedTab="request" />
          </div>

          <div className="flex">
            <NetworkLogsDropdown
              selectedCall={selectedNetworkCallIndex}
              onSelect={setSelectedNetworkCallIndex}
              logs={logs}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export const NetworkNLogsBlock = React.memo(NetworkNLogsBlockComponent)
